import type { ViewerProvider, ViewerLaunchParams, ViewerConfig, ViewerSyncAdapter, SyncCapabilitySet } from '../types';
import type { MonitorChannel } from '../../multi-monitor/MonitorChannel';
import { GWTSyncAdapter } from '../../multi-monitor/GWTSyncAdapter';

/**
 * Capabilities the GWT viewer supports via BritSyncBridge.java.
 * Scroll and W/L sync use the GWT viewer's Cornerstone.js internals
 * through JsInterop. Tool, crosshair, and annotation sync are not
 * feasible because the GWT viewer manages those through its own
 * Java-based tool system.
 */
const GWT_SYNC_CAPABILITIES: SyncCapabilitySet = {
  scroll: true,
  windowLevel: true,
  tool: false,
  crosshair: false,
  annotations: false,
};

/**
 * GWT (legacy) viewer provider — iframe-based.
 *
 * Launches the original GWT-compiled PACS viewer inside its own
 * lightweight nginx container.  The flow:
 *
 *   1. brit-web proxies /gwt/ → gwt-viewer container (static assets)
 *   2. gwt-viewer serves index.html which loads config-loader.js
 *   3. config-loader.js fetches /gwt-launch?... from the Java backend
 *      to get Works-* meta values, injects them into the DOM, then
 *      bootstraps the GWT module (com.brit.gwt.PacsClient.nocache.js)
 *
 * No hanging protocol or comparison support — this is the legacy viewer.
 */
export const GWTViewerProvider: ViewerProvider = {
  id: 'gwt',
  name: 'GWT Viewer',
  supportsHangingProtocols: false,
  supportsComparisons: false,
  syncCapabilities: GWT_SYNC_CAPABILITIES,

  buildViewerConfig({ study, siteId, sessionId }: ViewerLaunchParams): ViewerConfig {
    const params = new URLSearchParams({
      siteid: siteId,
      action: 'VIEWSTUDY',
      objKey: study.objKey,
      session: sessionId,
    });

    // /gwt/ routes through brit-web nginx → gwt-viewer container.
    // config-loader.js inside the container will forward these params
    // to /gwt-launch to retrieve the Works-* meta configuration.
    return {
      type: 'iframe' as const,
      url: `/gwt/?${params.toString()}`,
    };
  },

  createSyncAdapter(
    channel: MonitorChannel,
    windowId: string,
    iframeRef: () => HTMLIFrameElement | null,
  ): ViewerSyncAdapter {
    return new GWTSyncAdapter(channel, windowId, iframeRef);
  },
};
